/**
 * SessionListItem - Presentation component for a single session item
 * Follows ADR-004: Separation of Concerns - Pure presentation layer
 */

import React from 'react';
import { Calendar, Trash2 } from 'lucide-react';
import { cn } from '../utils/cn';

/**
 * Props for SessionListItem
 */
interface SessionListItemProps {
  /** Session ID */
  id: string;
  /** Session title */
  title: string;
  /** Formatted date string */
  dateString: string;
  /** Whether this session is selected */
  isSelected: boolean;
  /** Whether delete is allowed */
  allowDelete: boolean;
  /** Delete button tooltip */
  deleteTooltip: string;
  /** Click handler */
  onSelect: (id: string) => void;
  /** Delete handler */
  onDelete: () => void;
}

/**
 * Single session item in the list
 */
export const SessionListItem: React.FC<SessionListItemProps> = ({
  id,
  title,
  dateString,
  isSelected,
  allowDelete,
  deleteTooltip,
  onSelect,
  onDelete
}) => {
  return (
    <div
      className={cn(
        'chatbot-session-item group relative p-4 rounded-lg cursor-pointer transition-all duration-200 select-none',
        isSelected
          ? 'chatbot-session-item-active bg-primary/10 border-2 border-primary shadow-sm'
          : 'bg-background border border-border hover:border-primary/50 hover:shadow-sm'
      )}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect(id);
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className={cn(
            'chatbot-session-item-title text-sm font-semibold truncate',
            isSelected ? 'text-primary' : 'text-foreground'
          )}>
            {title}
          </h3>
          <div className={cn(
            'chatbot-session-item-date flex items-center mt-1.5 text-xs',
            isSelected ? 'text-primary/70' : 'text-muted-foreground'
          )}>
            <Calendar size={12} className="mr-1.5 flex-shrink-0" />
            <span>{dateString}</span>
          </div>
        </div>

        {allowDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className={cn(
              'chatbot-session-delete-button opacity-0 group-hover:opacity-100 p-1.5 rounded-md transition-all',
              'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
            )}
            title={deleteTooltip}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

